import {codeMirrorPhrases} from "../codemirror_i18n.js"
/**
 * AppEditor hook — CodeMirror 6 editor for Seiva Apps.
 *
 * Uses phx-update="ignore" to protect CodeMirror DOM.
 * File switching via handleEvent("load_file", ...) from server.
 * CodeMirror is bundled via npm — no CDN race conditions.
 */

import { EditorView, basicSetup } from "codemirror"
import { EditorState } from "@codemirror/state"
import { javascript } from "@codemirror/lang-javascript"
import { css } from "@codemirror/lang-css"
import { json } from "@codemirror/lang-json"
import { html } from "@codemirror/lang-html"
import { elixir } from "codemirror-lang-elixir"
import { oneDark } from "@codemirror/theme-one-dark"
import { keymap } from "@codemirror/view"
import { githubDark, githubLight } from "@uiw/codemirror-theme-github"
import { dracula } from "@uiw/codemirror-theme-dracula"
import { nord } from "@uiw/codemirror-theme-nord"

const THEMES = {
  "vs-dark": oneDark,
  "one-dark": oneDark,
  "github-dark": githubDark,
  "github-light": githubLight,
  "dracula": dracula,
  "nord": nord,
  "light": null, // no theme extension = default light
}

function langExt(lang) {
  switch (lang) {
    case "typescriptreact": case "typescript": case "tsx": case "ts":
      return javascript({ jsx: true, typescript: true })
    case "javascriptreact": case "javascript": case "jsx": case "js":
      return javascript({ jsx: true })
    case "css": return css()
    case "json": return json()
    case "html": return html()
    case "elixir": case "ex": case "exs":
      return elixir()
    default: return javascript({ jsx: true, typescript: true })
  }
}

export default {
  mounted() {
    this._currentFileId = null
    this._ignoreUpdate = false
    this._themeName = localStorage.getItem("seiva_editor_theme") || "dracula"

    const baseTheme = EditorView.theme({
      "&": { height: "100%", fontSize: "13px" },
      ".cm-scroller": { overflow: "auto", fontFamily: "'JetBrains Mono', 'Fira Code', monospace" },
      ".cm-content": { padding: "8px 0" },
    })

    const makeState = (content, lang) => {
      const extensions = [
        basicSetup,
        EditorState.phrases.of(codeMirrorPhrases()),
        langExt(lang),
        baseTheme,
        keymap.of([{
          key: "Mod-s",
          run: () => { this.pushEvent("save_file", {}); return true }
        }]),
        EditorView.updateListener.of((update) => {
          if (update.docChanged && !this._ignoreUpdate) {
            this.pushEvent("code_changed", { value: update.state.doc.toString() })
          }
        }),
      ]
      const themeExt = THEMES[this._themeName]
      if (themeExt) extensions.push(themeExt)
      return EditorState.create({ doc: content || "", extensions })
    }

    // Create editor immediately (CodeMirror is bundled, no async loading)
    this.view = new EditorView({
      state: makeState("", "typescript"),
      parent: this.el,
    })
    this._makeState = makeState

    // Theme switching from server
    this.handleEvent("set_editor_theme", ({ theme }) => {
      this._themeName = theme
      localStorage.setItem("seiva_editor_theme", theme)
      const content = this.view.state.doc.toString()
      const lang = this.el.dataset.language || "typescript"
      this._ignoreUpdate = true
      this.view.setState(this._makeState(content, lang))
      this._ignoreUpdate = false
    })

    // Server pushes file data — this is how we switch files
    this.handleEvent("load_file", ({ file_id, file_path, language, content }) => {
      if (!this.view || file_id === this._currentFileId) return
      this._currentFileId = file_id
      this._ignoreUpdate = true
      this.view.setState(this._makeState(content || "", language || "typescript"))
      this._ignoreUpdate = false
      this.view.focus()
    })

    // Server pushes updated content for current file (after save or AI edit)
    this.handleEvent("refresh_content", ({ content }) => {
      if (!this.view) return
      const current = this.view.state.doc.toString()
      if (current !== (content || "")) {
        this._ignoreUpdate = true
        this.view.dispatch({
          changes: { from: 0, to: current.length, insert: content || "" }
        })
        this._ignoreUpdate = false
      }
    })

    // Load initial file from data attributes
    const fileId = this.el.dataset.fileId
    if (fileId) {
      this._currentFileId = fileId
      this._ignoreUpdate = true
      this.view.setState(this._makeState(
        this.el.dataset.content || "",
        this.el.dataset.language || "typescript"
      ))
      this._ignoreUpdate = false
    }

  },

  // With phx-update="ignore", updated() is NOT called — that's the point.
  // All communication happens via handleEvent.

  destroyed() {
    if (this.view) this.view.destroy()
  }
}
